import {
  ContactId,
  ShipReplacementTo,
  ReplacementType,
  TypeOfLoss,
  WaysToContact,
} from "./FieldIds";
import classNames from "classnames";
import { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-grid-system";
import { ToolType } from "../AccountDetails";
import { toolTypeToName } from "../AccountDetails/utils";
import Container from "../common/Container";
import StyledButton from "../common/buttons/StyledButton";
import toolStyles from "../../sharedStyles/Tool.module.css";
import styles from "./LostStolen.module.css";
import LostStolenForm from "./LostStolenForm";
import {
  DateString,
  GetAccountResponse,
  LostStolenScript,
  ValidationErrors,
} from "../../lib/api/models";
import {
  useGetLostStolenOptions,
  useSubmitLostStolen,
} from "../../lib/api/queries";
import { useChannelState } from "../../lib/channelContext";
import { ChannelMapToFull } from "../../lib/channelUtil";
import Spinner from "../common/Spinner";
import StyledError from "../common/StyledError";
import { getLostStolenScriptDetails } from "../../lib/scripts/lostStolen/lostStolenScripts";
import ConversationScript from "../common/ConversationScript";
import StyledCheckbox from "../common/inputs/StyledCheckbox";
import StyledRadioButton from "../common/inputs/StyledRadioButton";
import { setValidationErrorsAndDisplayGeneralErrors } from "../../lib/validation";
import {
  additionalInfoRegex,
  addressRegex,
  cityRegex,
  fraudChargesRegex,
  PHONE_VALUE_LENGTH,
  ZIPCODE_LENGTH,
  ZIPCODE_PLUS_FOUR_LENGTH,
} from "./constants";
import { HTTPError } from "../../lib/api/endpoints";
import { format } from "date-fns";

const strings = {
  submit: "Submit",
  close: "Close",
  errorTitle: "Failed to load Lost/Stolen",
  successful: "Lost Report Successful",
  emergencyCard: "Emergency Replacement Card",
  traveling: "Traveling in the next 5 business days?",
  yes: "Yes",
  no: "No",

  successfullySubmitted: "Successfully submitted lost/stolen report",
  successfullyTransferred:
    "Successfully transferred lost/stolen report to security",
};

type FinalInfo = {
  replacementOption?: ReplacementType;
  waiveReplacementFee?: boolean;
  waiveRushFee?: boolean;
  dateLost?: DateString;
  lossLocation?: string;
  stateOfLoss?: string;
  pinCompromised?: boolean;
  fraudCharges?: string;
};

type ShipReplacementToBilling = {
  addressChangeToday?: boolean;
  newAddressVerify?: boolean;
  cid?: string;

  finalInfo?: FinalInfo;
};

type ShipReplacementToTemporary = {
  cid?: string;
  tempAddressVerify?: boolean;
  reasonForTempAddress?: string;
  tempAddress?: {
    address1?: string;
    address2?: string;
    zip?: string;
    city?: string;
    state?: string;
  };

  finalInfo?: FinalInfo;
};

type EarlyTerminationInfo = {
  additionalInfo?: string;
  shipReplacementTo?: ShipReplacementTo;

  finalInfo?: FinalInfo;
};

export type LostStolenFormState = {
  contactId?: ContactId;
  typeOfLoss?: TypeOfLoss;
  fraudOnAccount?: boolean;

  earlyTermination?: EarlyTerminationInfo;

  yesFraudOnAccount?: {
    transferToSecurity?: boolean;
    cid?: string;
    bestWayToContact?: string;
    otherPhone?: string;
    shipReplacementTo?: ShipReplacementTo;
    billing?: ShipReplacementToBilling;
    temporary?: ShipReplacementToTemporary;
    noShipReplacementToFinalInfo?: FinalInfo;
  };

  noFraudOnAccount?: {
    shipReplacementTo?: ShipReplacementTo;
    billing?: ShipReplacementToBilling;
    temporary?: ShipReplacementToTemporary;
  };
};

type Props = {
  className?: string;
  toolId: string;
  account: GetAccountResponse;
  onRequestClose: (bypassProgressConfirmation?: boolean) => void;
  onDisplayGeneralErrors: (error: unknown) => void;
  onSetToolInProgress: (id: string, inProgress: boolean) => void;
  onSuccess: (summaryWithoutTime: string) => void;
  referenceId?: string;
};

export default function LostStolen({
  className,
  toolId,
  account,
  onRequestClose,
  onDisplayGeneralErrors,
  onSetToolInProgress,
  onSuccess,
  referenceId,
}: Props) {
  const { channel } = useChannelState();
  const [formState, setFormState] = useState<LostStolenFormState>({});
  const [traveling, setTraveling] = useState<boolean>();
  const [emergencyCardChecked, setEmergencyCardChecked] = useState(false);
  const [networkValidationMessages, setNetworkValidationMessages] =
    useState<ValidationErrors>();

  const clearNetworkValidationMessage = (...keys: string[]) => {
    if (!networkValidationMessages || keys.length < 1) {
      return;
    }

    setNetworkValidationMessages((prev) => {
      const next = { ...prev };
      keys.forEach((key) => {
        next[key] = [];
      });

      return next;
    });
  };

  const {
    data: getLostStolenOptionsResponse,
    isLoading: isGetLostStolenOptionsResponseLoading,
    isError: isGetLostStolenOptionsResponseError,
    error: getLostStolenOptionsResponseError,
    refetch: getLostStolenOptionsResponseRefetch,
  } = useGetLostStolenOptions(account.currentDwbuid);

  const hasInactiveStatus =
    getLostStolenOptionsResponse?.externalStatus === "B" ||
    getLostStolenOptionsResponse?.externalStatus === "Z";
  const hasLostStolenExtStatus =
    getLostStolenOptionsResponse?.externalStatus === "L" ||
    getLostStolenOptionsResponse?.externalStatus === "U";
  const hasInvalidStatus =
    getLostStolenOptionsResponse?.externalStatus === "C" ||
    getLostStolenOptionsResponse?.externalStatus === "E" ||
    getLostStolenOptionsResponse?.externalStatus === "F" ||
    getLostStolenOptionsResponse?.externalStatus === "I";

  useEffect(() => {
    if (!getLostStolenOptionsResponse) {
      return;
    }

    if (
      formState.yesFraudOnAccount?.transferToSecurity ||
      (formState.contactId !== ContactId.Cardmember &&
        (hasInactiveStatus || hasLostStolenExtStatus || hasInvalidStatus)) ||
      (formState.contactId === ContactId.Cardmember &&
        (getLostStolenOptionsResponse?.isLoanAccount ||
          hasInactiveStatus ||
          (hasInvalidStatus && formState.fraudOnAccount === false) ||
          (hasLostStolenExtStatus &&
            (formState.fraudOnAccount === false ||
              formState.yesFraudOnAccount?.transferToSecurity === false)) ||
          (getLostStolenOptionsResponse.externalStatus === "A" &&
            getLostStolenOptionsResponse.reasonCode === "62" &&
            formState.fraudOnAccount === false &&
            (formState.typeOfLoss === TypeOfLoss.LostCard ||
              formState.typeOfLoss === TypeOfLoss.Stolen ||
              formState.typeOfLoss === TypeOfLoss.NotReceived))))
    ) {
      onSetToolInProgress(toolId, false);
    } else if (
      formState.contactId &&
      !(
        getLostStolenOptionsResponse.isLoanAccount &&
        (formState.contactId === ContactId.Cardmember ||
          formState.contactId === ContactId.Merchant ||
          formState.contactId === ContactId.ThirdPartyUnknownToCM)
      )
    ) {
      onSetToolInProgress(toolId, true);
    }
  }, [
    formState.contactId,
    onSetToolInProgress,
    toolId,
    getLostStolenOptionsResponse,
    formState.yesFraudOnAccount?.transferToSecurity,
    formState.fraudOnAccount,
    formState.typeOfLoss,
    hasInactiveStatus,
    hasLostStolenExtStatus,
    hasInvalidStatus,
  ]);

  const {
    mutateAsync: submitLostStolen,
    isLoading: isSubmitting,
    isSuccess,
    isError,
    error: submitLostStolenError,
  } = useSubmitLostStolen();

  const scriptDetails = useMemo(() => {
    if (getLostStolenOptionsResponse) {
      let scriptName: LostStolenScript | undefined = undefined;
      if (
        isError &&
        submitLostStolenError instanceof HTTPError &&
        submitLostStolenError.status >= 500
      ) {
        scriptName = "LS0010";
      } else if (formState.contactId) {
        if (isSuccess) {
          if (
            formState.contactId === ContactId.Merchant ||
            formState.contactId === ContactId.ThirdPartyUnknownToCM
          ) {
            scriptName = "LS0033";
          } else if (
            formState.contactId === ContactId.AuthUser ||
            formState.contactId === ContactId.Other ||
            formState.contactId === ContactId.Parent ||
            formState.contactId === ContactId.ThirdPartyKnownToCM
          ) {
            scriptName = "LS0029";
          } else {
            scriptName = "LS0002";
          }
        } else if (
          getLostStolenOptionsResponse.isLoanAccount &&
          (formState.contactId === ContactId.Cardmember ||
            formState.contactId === ContactId.Merchant ||
            formState.contactId === ContactId.ThirdPartyUnknownToCM)
        ) {
          scriptName = "LS0006";
        } else if (
          formState.fraudOnAccount === false &&
          formState.contactId === ContactId.Cardmember &&
          (getLostStolenOptionsResponse.externalStatus === "" ||
            (getLostStolenOptionsResponse.externalStatus === "A" &&
              getLostStolenOptionsResponse.reasonCode !== "62"))
        ) {
          scriptName = "LS0011";
        } else if (
          (formState.contactId === ContactId.ThirdPartyKnownToCM ||
            formState.contactId === ContactId.Parent ||
            formState.contactId === ContactId.AuthUser ||
            formState.contactId === ContactId.Other) &&
          (getLostStolenOptionsResponse.externalStatus === "" ||
            (getLostStolenOptionsResponse.externalStatus === "A" &&
              getLostStolenOptionsResponse.reasonCode !== "62"))
        ) {
          scriptName = "LS0020";
        } else if (formState.contactId === ContactId.Cardmember) {
          if (
            getLostStolenOptionsResponse.externalStatus === "B" ||
            getLostStolenOptionsResponse.externalStatus === "Z"
          ) {
            scriptName = "LS0007";
          } else if (
            getLostStolenOptionsResponse.externalStatus === "L" ||
            getLostStolenOptionsResponse.externalStatus === "U"
          ) {
            scriptName = "LS0009";
          } else if (
            (getLostStolenOptionsResponse.externalStatus === "A" &&
              getLostStolenOptionsResponse.reasonCode === "62") ||
            getLostStolenOptionsResponse.externalStatus === "C" ||
            getLostStolenOptionsResponse.externalStatus === "E" ||
            getLostStolenOptionsResponse.externalStatus === "F" ||
            getLostStolenOptionsResponse.externalStatus === "I"
          ) {
            if (formState.typeOfLoss === TypeOfLoss.FraudApp) {
              scriptName = "LS0039";
            } else if (
              formState.typeOfLoss === TypeOfLoss.Counterfeit ||
              formState.typeOfLoss === TypeOfLoss.AccountTakeover ||
              formState.typeOfLoss === TypeOfLoss.AllMailAndPhone
            ) {
              if (formState.yesFraudOnAccount?.transferToSecurity === false) {
                scriptName = "LS0011";
              } else {
                scriptName = "LS0056";
              }
            } else {
              scriptName = "LS0019";
            }
          } else if (formState.typeOfLoss) {
            scriptName = "LS0024";
            if (formState.typeOfLoss === TypeOfLoss.FraudApp) {
              scriptName = "LS0039";
            } else if (
              formState.typeOfLoss === TypeOfLoss.AccountTakeover ||
              formState.typeOfLoss === TypeOfLoss.Counterfeit ||
              formState.typeOfLoss === TypeOfLoss.AllMailAndPhone
            ) {
              if (formState.yesFraudOnAccount?.transferToSecurity === false) {
                scriptName = "LS0011";
              } else {
                scriptName = "LS0056";
              }
            } else if (getLostStolenOptionsResponse.isClosedAccount) {
              scriptName = "LS0019";
            } else if (
              (formState.typeOfLoss === TypeOfLoss.NotReceived &&
                (formState.yesFraudOnAccount?.transferToSecurity === false ||
                  formState.fraudOnAccount === false)) ||
              ((getLostStolenOptionsResponse.externalStatus === "" ||
                (getLostStolenOptionsResponse.externalStatus === "A" &&
                  getLostStolenOptionsResponse.reasonCode !== "62")) &&
                (formState.typeOfLoss === TypeOfLoss.LostCard ||
                  formState.typeOfLoss === TypeOfLoss.Stolen) &&
                formState.yesFraudOnAccount?.transferToSecurity === false)
            ) {
              scriptName = "LS0011";
            } else if (
              getLostStolenOptionsResponse.externalStatus === "A" &&
              getLostStolenOptionsResponse.reasonCode !== "62"
            ) {
              scriptName = "LS0024";
            }
          }
        } else if (
          formState.contactId === ContactId.AuthUser ||
          formState.contactId === ContactId.Parent ||
          formState.contactId === ContactId.ThirdPartyKnownToCM ||
          formState.contactId === ContactId.Other ||
          formState.contactId === ContactId.Merchant ||
          formState.contactId === ContactId.ThirdPartyUnknownToCM
        ) {
          if (
            (getLostStolenOptionsResponse.externalStatus === "A" &&
              getLostStolenOptionsResponse.reasonCode === "62") ||
            getLostStolenOptionsResponse.externalStatus === "B" ||
            getLostStolenOptionsResponse.externalStatus === "Z" ||
            getLostStolenOptionsResponse.externalStatus === "L" ||
            getLostStolenOptionsResponse.externalStatus === "U" ||
            getLostStolenOptionsResponse.externalStatus === "C" ||
            getLostStolenOptionsResponse.externalStatus === "E" ||
            getLostStolenOptionsResponse.externalStatus === "F" ||
            getLostStolenOptionsResponse.externalStatus === "I"
          ) {
            scriptName = "LS0008";
          } else if (getLostStolenOptionsResponse.isClosedAccount) {
            scriptName = "LS0020";
          } else if (formState.fraudOnAccount === false) {
            scriptName = "LS0011";
          } else if (
            formState.contactId === ContactId.Merchant ||
            formState.contactId === ContactId.ThirdPartyUnknownToCM
          ) {
            scriptName = "LS0038";
          }
        }
      }

      return getLostStolenScriptDetails(
        formState,
        getLostStolenOptionsResponse,
        traveling,
        emergencyCardChecked,
        scriptName,
      );
    }

    return undefined;
  }, [
    getLostStolenOptionsResponse,
    submitLostStolenError,
    formState,
    isSuccess,
    isError,
    emergencyCardChecked,
    traveling,
  ]);

  const hideFieldsIfFraudApp =
    formState.contactId === ContactId.Cardmember &&
    formState.typeOfLoss === TypeOfLoss.FraudApp &&
    formState.yesFraudOnAccount?.transferToSecurity === false;

  const hideFieldsIfNotReceivedOrStolen =
    getLostStolenOptionsResponse?.externalStatus === "A" &&
    getLostStolenOptionsResponse?.reasonCode === "62" &&
    formState.contactId === ContactId.Cardmember &&
    (formState.typeOfLoss === TypeOfLoss.NotReceived ||
      formState.typeOfLoss === TypeOfLoss.Stolen) &&
    formState.yesFraudOnAccount?.transferToSecurity === false;

  const hideFieldsIfInvalidStatus =
    hasInvalidStatus &&
    formState.contactId === ContactId.Cardmember &&
    formState.fraudOnAccount &&
    formState.yesFraudOnAccount?.transferToSecurity === false;

  const hideShipReplacementTo =
    hideFieldsIfFraudApp ||
    hideFieldsIfNotReceivedOrStolen ||
    hideFieldsIfInvalidStatus;

  const prefillWithoutAddress =
    formState.contactId === ContactId.Cardmember &&
    (hasInvalidStatus ||
      (getLostStolenOptionsResponse?.externalStatus === "A" &&
        getLostStolenOptionsResponse?.reasonCode === "62") ||
      ((getLostStolenOptionsResponse?.externalStatus === "" ||
        getLostStolenOptionsResponse?.externalStatus === "A") &&
        formState.typeOfLoss === TypeOfLoss.FraudApp));

  function onSubmit() {
    if (!formState.contactId || !getLostStolenOptionsResponse) {
      return;
    }

    const fraudOnAccount = !!formState.fraudOnAccount;
    const useBillingAddress =
      formState.yesFraudOnAccount?.shipReplacementTo ===
        ShipReplacementTo.Billing ||
      formState.noFraudOnAccount?.shipReplacementTo ===
        ShipReplacementTo.Billing;
    const tempAddress =
      formState.yesFraudOnAccount?.temporary?.tempAddress ??
      formState.noFraudOnAccount?.temporary?.tempAddress;
    const finalInfo = formState.earlyTermination
      ? formState.earlyTermination.finalInfo
      : hideShipReplacementTo
        ? formState.yesFraudOnAccount?.noShipReplacementToFinalInfo
        : fraudOnAccount
          ? useBillingAddress
            ? formState.yesFraudOnAccount?.billing?.finalInfo
            : formState.yesFraudOnAccount?.temporary?.finalInfo
          : useBillingAddress
            ? formState.noFraudOnAccount?.billing?.finalInfo
            : formState.noFraudOnAccount?.temporary?.finalInfo;

    setNetworkValidationMessages({});

    submitLostStolen({
      dwbuid: account.currentDwbuid,
      addressChange: fraudOnAccount
        ? formState.yesFraudOnAccount?.billing?.addressChangeToday
        : formState.noFraudOnAccount?.billing?.addressChangeToday,
      cid: fraudOnAccount
        ? formState.yesFraudOnAccount?.cid
          ? formState.yesFraudOnAccount.cid
          : useBillingAddress
            ? // this should never trigger since any fraud on account would be transferred to security or require a CID
              formState.yesFraudOnAccount?.billing?.cid
            : formState.yesFraudOnAccount?.temporary?.cid
        : useBillingAddress
          ? formState.noFraudOnAccount?.billing?.cid
          : formState.noFraudOnAccount?.temporary?.cid,
      contactId: formState.contactId,
      dateLost: finalInfo?.dateLost,
      lossLocationId: finalInfo?.lossLocation,
      lossStateId: finalInfo?.stateOfLoss,
      pinCompromised: finalInfo?.pinCompromised,
      replacementOptionId: finalInfo?.replacementOption,
      shipToId: formState.earlyTermination
        ? formState.earlyTermination.shipReplacementTo
        : fraudOnAccount
          ? formState.yesFraudOnAccount?.shipReplacementTo
          : formState.noFraudOnAccount?.shipReplacementTo,
      typeOfLossId: formState.typeOfLoss,
      waiveReplacementFee: finalInfo?.waiveReplacementFee,
      waiveRushFee: finalInfo?.waiveRushFee,
      additionalInfo: formState.earlyTermination?.additionalInfo
        ? formState.earlyTermination.additionalInfo
        : fraudOnAccount
          ? hideShipReplacementTo
            ? formState.yesFraudOnAccount?.noShipReplacementToFinalInfo
                ?.fraudCharges
            : useBillingAddress
              ? formState.yesFraudOnAccount?.billing?.finalInfo?.fraudCharges
              : formState.yesFraudOnAccount?.temporary?.finalInfo?.fraudCharges
          : undefined,
      bestWayToContactTypeId: fraudOnAccount
        ? formState.yesFraudOnAccount?.bestWayToContact
        : undefined,
      wayToContactPhone:
        formState.yesFraudOnAccount?.bestWayToContact === WaysToContact.Home
          ? getLostStolenOptionsResponse?.homePhone
          : formState.yesFraudOnAccount?.bestWayToContact === WaysToContact.Work
            ? getLostStolenOptionsResponse.workPhone
            : formState.yesFraudOnAccount?.otherPhone,
      address:
        !useBillingAddress && tempAddress?.address1
          ? {
              addressLine1: tempAddress?.address1,
              addressLine2: tempAddress?.address2,
              city: tempAddress?.city,
              state: tempAddress?.state,
              zip: tempAddress?.zip,
            }
          : undefined,
      referenceId,
      addressChangeTodayVerified: formState.fraudOnAccount
        ? formState.yesFraudOnAccount?.billing?.newAddressVerify
        : formState.noFraudOnAccount?.billing?.newAddressVerify,
      possibleFraudOnAccount: formState.fraudOnAccount ?? null,
      transferToSecurity: formState.fraudOnAccount ? false : null,
      tempAddressReasonId: formState.fraudOnAccount
        ? formState.yesFraudOnAccount?.temporary?.reasonForTempAddress
        : formState.noFraudOnAccount?.temporary?.reasonForTempAddress,
      tempAddressVerified: formState.fraudOnAccount
        ? formState.yesFraudOnAccount?.temporary?.tempAddressVerify
        : formState.noFraudOnAccount?.temporary?.tempAddressVerify,
      channel: ChannelMapToFull[channel],
    })
      .then(() => {
        onSetToolInProgress(toolId, false);
      })
      .catch((err) =>
        setValidationErrorsAndDisplayGeneralErrors(
          err,
          setNetworkValidationMessages,
          onDisplayGeneralErrors,
        ),
      );
  }

  const tempAddress = formState.fraudOnAccount
    ? formState.yesFraudOnAccount?.temporary?.tempAddress
    : formState.noFraudOnAccount?.temporary?.tempAddress;

  const finalInfo = formState.earlyTermination
    ? formState.earlyTermination.finalInfo
    : hideShipReplacementTo
      ? formState.yesFraudOnAccount?.noShipReplacementToFinalInfo
      : formState.fraudOnAccount
        ? hideShipReplacementTo || prefillWithoutAddress
          ? formState.yesFraudOnAccount?.noShipReplacementToFinalInfo
          : formState.yesFraudOnAccount?.shipReplacementTo ===
              ShipReplacementTo.Billing
            ? formState.yesFraudOnAccount?.billing?.finalInfo
            : formState.yesFraudOnAccount?.temporary?.finalInfo
        : formState.noFraudOnAccount?.shipReplacementTo ===
            ShipReplacementTo.Billing
          ? formState.noFraudOnAccount?.billing?.finalInfo
          : formState.noFraudOnAccount?.temporary?.finalInfo;

  const isSubmitDisabled =
    isGetLostStolenOptionsResponseLoading ||
    isSuccess ||
    // OR type of loss not filled in
    !formState.typeOfLoss ||
    // OR CID not filled in
    (formState.fraudOnAccount &&
      formState.yesFraudOnAccount?.cid?.length !== PHONE_VALUE_LENGTH) ||
    (formState.noFraudOnAccount?.billing?.newAddressVerify === false &&
      formState.noFraudOnAccount.billing.cid?.length !== PHONE_VALUE_LENGTH) ||
    (formState.noFraudOnAccount?.temporary?.tempAddressVerify === false &&
      formState.noFraudOnAccount.temporary.cid?.length !==
        PHONE_VALUE_LENGTH) ||
    // OR "Best Way to Contact: Other" phone input not filled in
    (formState.fraudOnAccount &&
      formState.yesFraudOnAccount?.bestWayToContact === WaysToContact.Other &&
      (!formState.yesFraudOnAccount.otherPhone ||
        formState.yesFraudOnAccount.otherPhone.length !==
          PHONE_VALUE_LENGTH)) ||
    // OR address change today not selected
    (formState.yesFraudOnAccount?.shipReplacementTo ===
      ShipReplacementTo.Billing &&
      formState.yesFraudOnAccount.billing?.addressChangeToday === undefined) ||
    (formState.noFraudOnAccount?.shipReplacementTo ===
      ShipReplacementTo.Billing &&
      formState.noFraudOnAccount.billing?.addressChangeToday === undefined) ||
    // OR temp address is not filled in
    (formState.yesFraudOnAccount?.shipReplacementTo ===
      ShipReplacementTo.Temporary &&
      !(
        formState.yesFraudOnAccount?.temporary?.tempAddress?.address1 &&
        formState.yesFraudOnAccount?.temporary?.tempAddress?.city &&
        formState.yesFraudOnAccount?.temporary?.tempAddress?.state &&
        formState.yesFraudOnAccount?.temporary?.tempAddress?.zip &&
        (formState.yesFraudOnAccount.temporary.tempAddress.zip.length ===
          ZIPCODE_LENGTH ||
          formState.yesFraudOnAccount.temporary.tempAddress.zip.length ===
            ZIPCODE_PLUS_FOUR_LENGTH)
      )) ||
    (formState.noFraudOnAccount?.shipReplacementTo ===
      ShipReplacementTo.Temporary &&
      !(
        formState.noFraudOnAccount?.temporary?.tempAddress?.address1 &&
        formState.noFraudOnAccount?.temporary?.tempAddress?.city &&
        formState.noFraudOnAccount?.temporary?.tempAddress?.state &&
        formState.noFraudOnAccount?.temporary?.tempAddress?.zip &&
        (formState.noFraudOnAccount.temporary.tempAddress.zip.length ===
          ZIPCODE_LENGTH ||
          formState.noFraudOnAccount.temporary.tempAddress.zip.length ===
            ZIPCODE_PLUS_FOUR_LENGTH)
      )) ||
    // OR lost/stolen final fields not filled in
    (!hideShipReplacementTo &&
      !prefillWithoutAddress &&
      finalInfo?.waiveReplacementFee === undefined) ||
    (finalInfo?.replacementOption &&
      finalInfo.replacementOption !== ReplacementType.Standard &&
      finalInfo?.waiveRushFee === undefined) ||
    finalInfo?.dateLost === undefined ||
    finalInfo.dateLost === "" ||
    finalInfo?.dateLost >
      (format(new Date(), "yyyy-MM-dd").toString() as DateString) ||
    finalInfo?.lossLocation === undefined ||
    finalInfo?.stateOfLoss === undefined ||
    (!(
      formState.contactId === ContactId.Cardmember &&
      (((hasInvalidStatus ||
        getLostStolenOptionsResponse?.externalStatus === "" ||
        getLostStolenOptionsResponse?.externalStatus === "A") &&
        formState.typeOfLoss === TypeOfLoss.FraudApp) ||
        formState.typeOfLoss === TypeOfLoss.Counterfeit ||
        formState.typeOfLoss === TypeOfLoss.AccountTakeover ||
        formState.typeOfLoss === TypeOfLoss.AllMailAndPhone)
    ) &&
      !(
        getLostStolenOptionsResponse?.externalStatus === "" &&
        (formState.contactId === ContactId.ThirdPartyUnknownToCM ||
          formState.contactId === ContactId.Merchant)
      ) &&
      finalInfo?.pinCompromised === undefined) ||
    (formState.fraudOnAccount && !finalInfo?.fraudCharges) ||
    // OR any regex field validation is failing
    !addressRegex.test(tempAddress?.address1 ?? "") ||
    !addressRegex.test(tempAddress?.address2 ?? "") ||
    !cityRegex.test(tempAddress?.city ?? "") ||
    !fraudChargesRegex.test(finalInfo?.fraudCharges ?? "") ||
    !additionalInfoRegex.test(formState.earlyTermination?.additionalInfo ?? "");

  return (
    <div className={classNames(toolStyles.container, className)} id={toolId}>
      <Container>
        <Row
          className={classNames(
            toolStyles.header,
            toolStyles.headerWithDivider,
          )}
        >
          <Col
            className={toolStyles.centerCol}
            md={
              isGetLostStolenOptionsResponseLoading ||
              isGetLostStolenOptionsResponseError
                ? 11
                : 9
            }
          >
            <h2 className={toolStyles.title}>
              {toolTypeToName(ToolType.LostStolen)}
            </h2>
          </Col>
          {!isGetLostStolenOptionsResponseLoading &&
            !isGetLostStolenOptionsResponseError && (
              <Col md={2}>
                <StyledButton
                  className={styles.submitButton}
                  isLoading={isSubmitting}
                  onClick={onSubmit}
                  disabled={isSubmitDisabled}
                >
                  {strings.submit}
                </StyledButton>
              </Col>
            )}
          <Col md={1}>
            <StyledButton
              className={toolStyles.headerButton}
              variant="secondary"
              onClick={() => {
                if (isSuccess) {
                  onSuccess(strings.successfullySubmitted);
                } else if (formState.yesFraudOnAccount?.transferToSecurity) {
                  onSuccess(strings.successfullyTransferred);
                } else {
                  onRequestClose();
                }
              }}
            >
              {strings.close}
            </StyledButton>
          </Col>
        </Row>
        {isGetLostStolenOptionsResponseLoading ? (
          <Row>
            <Col>
              <Spinner />
            </Col>
          </Row>
        ) : isGetLostStolenOptionsResponseError ? (
          <Row>
            <Col>
              <StyledError
                errorTitle={strings.errorTitle}
                error={getLostStolenOptionsResponseError}
                refetch={getLostStolenOptionsResponseRefetch}
              />
            </Col>
          </Row>
        ) : (
          <Row className={styles.row}>
            <Col md={4}>
              {isSuccess &&
              getLostStolenOptionsResponse?.isEmergencyCardEligible &&
              formState.typeOfLoss !== TypeOfLoss.NotReceived ? (
                <div>
                  {formState.contactId === ContactId.Cardmember ? (
                    <>
                      <div className={styles.radioContainer}>
                        <div className={styles.label}>{strings.traveling}</div>
                        <div>
                          <StyledRadioButton
                            className={styles.radioButton}
                            checked={!!traveling}
                            label={strings.yes}
                            onChange={() => setTraveling(true)}
                          />
                          <StyledRadioButton
                            className={styles.radioButton}
                            checked={traveling !== undefined && !traveling}
                            label={strings.no}
                            onChange={() => {
                              setTraveling(false);
                              setEmergencyCardChecked(false);
                            }}
                          />
                        </div>
                      </div>
                      <div className={styles.divider} />
                    </>
                  ) : null}
                  {traveling === true ? (
                    <StyledCheckbox
                      className={styles.emergencyCardCheckbox}
                      checked={emergencyCardChecked}
                      onChange={(e) =>
                        setEmergencyCardChecked(e.target.checked)
                      }
                      label={strings.emergencyCard}
                    />
                  ) : null}
                </div>
              ) : !isSuccess ? (
                <LostStolenForm
                  onTransferToSecurity={() =>
                    onSuccess(
                      "Successfully transferred lost/stolen report to security",
                    )
                  }
                  formState={formState}
                  setFormState={setFormState}
                  options={getLostStolenOptionsResponse}
                  isSubmitting={isSubmitting}
                  validationMessages={networkValidationMessages}
                  clearNetworkValidationMessage={clearNetworkValidationMessage}
                />
              ) : null}
            </Col>
            <Col md={8}>
              {scriptDetails ? (
                <ConversationScript
                  className={styles.scriptContainer}
                  scripts={scriptDetails.script}
                  scriptName={scriptDetails.name}
                />
              ) : null}
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
}
